import React, { useState, useEffect, useRef } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Alert,
  MenuItem,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import BuildIcon from "@mui/icons-material/Build";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import LoopIcon from "@mui/icons-material/Loop";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CodeIcon from "@mui/icons-material/Code";
import InputIcon from "@mui/icons-material/Input";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Node, Edge } from "reactflow";
import { WorkflowNodeData } from "./WorkflowNode";
import {
  CronScheduleBuilder,
  getScheduleLabel,
} from "./CronScheduleBuilder.tsx";
import { getWorkflowWebhookConfig, configureWorkflowWebhook } from "../../api/agentApi";
import { WebhookConfigBuilder } from "./WebhookConfigBuilder.tsx";

interface WorkflowPropertiesDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedElement: Node<WorkflowNodeData> | Edge | null;
  onUpdateNode?: (nodeId: string, updates: Partial<WorkflowNodeData>) => void;
  readonly?: boolean;
  nodes?: Node<WorkflowNodeData>[];
  workflowId?: string;
}

interface OutputField {
  name: string;
  type: string;
  description: string;
  jsonPath: string;
  sourceNodeLabel: string;
}

const WorkflowPropertiesDrawer: React.FC<WorkflowPropertiesDrawerProps> = ({
  open,
  onClose,
  selectedElement,
  onUpdateNode,
  readonly = false,
  nodes = [],
  workflowId,
}) => {
  const element = selectedElement;
  const isNode = element && "data" in element;

  // Get available outputs from predecessor nodes
  const getAvailableOutputs = (): OutputField[] => {
    if (!isNode || !element.data) return [];

    const currentNode = element as Node<WorkflowNodeData>;
    const dependsOn = currentNode.data.rawData?.depends_on || currentNode.data.config?.depends_on || [];

    if (!Array.isArray(dependsOn) || dependsOn.length === 0) return [];

    const allOutputs: OutputField[] = [];

    dependsOn.forEach((depId: string) => {
      const sourceNode = nodes.find(n => n.id === depId);
      if (!sourceNode) return;

      const nodeType = sourceNode.data.type;
      const nodeLabel = sourceNode.data.label || depId;

      switch (nodeType) {
        case 'agent':
        case 'tool':
          allOutputs.push({
            name: `${depId}_output`,
            type: 'string',
            description: `Full response from ${nodeLabel}`,
            jsonPath: `$.steps.${depId}.output`,
            sourceNodeLabel: nodeLabel,
          });
          break;

        case 'human_input':
          const inputFields = sourceNode.data.rawData?.input_fields || sourceNode.data.config?.input_fields || [];
          inputFields.forEach((field: any) => {
            allOutputs.push({
              name: `${depId}_${field.name}`,
              type: field.type,
              description: `User input from ${nodeLabel}: ${field.label || field.name}`,
              jsonPath: `$.steps.${depId}.output.${field.name}`,
              sourceNodeLabel: nodeLabel,
            });
          });
          break;

        case 'conditional':
          allOutputs.push({
            name: `${depId}_decision`,
            type: 'string',
            description: `Selected branch from ${nodeLabel}`,
            jsonPath: `$.steps.${depId}.output`,
            sourceNodeLabel: nodeLabel,
          });
          break;

        case 'merge':
          allOutputs.push({
            name: `${depId}_merged`,
            type: 'object',
            description: `Combined outputs from ${nodeLabel}`,
            jsonPath: `$.steps.${depId}.output`,
            sourceNodeLabel: nodeLabel,
          });
          break;

        case 'loop':
          allOutputs.push({
            name: `${depId}_iterations`,
            type: 'array',
            description: `Results from ${nodeLabel} iterations`,
            jsonPath: `$.steps.${depId}.output`,
            sourceNodeLabel: nodeLabel,
          });
          break;

        default:
          allOutputs.push({
            name: `${depId}_output`,
            type: 'any',
            description: `Output from ${nodeLabel}`,
            jsonPath: `$.steps.${depId}.output`,
            sourceNodeLabel: nodeLabel,
          });
          break;
      }
    });

    return allOutputs;
  };

  const availableOutputs = getAvailableOutputs();

  // Helper function to check if a parameter is from the tool schema
  const isToolParameter = (paramName: string): boolean => {
    if (!isNode || !element.data || element.data.type !== 'tool') return false;
    const toolParams = element.data.config.tool_parameters || element.data.rawData?.tool_parameters || [];
    return Array.isArray(toolParams) && toolParams.some((p: any) => p.name === paramName);
  };

  // Local state for editable fields
  const [editablePrompt, setEditablePrompt] = useState("");
  const [editableVersion, setEditableVersion] = useState("1");
  const [editableCron, setEditableCron] = useState("");
  const [editableOnFailure, setEditableOnFailure] = useState("");
  const [editableTitle, setEditableTitle] = useState("");
  const [editableTimeoutMinutes, setEditableTimeoutMinutes] = useState("");
  const [editableOnTimeout, setEditableOnTimeout] = useState("fail");
  const [editableInputFields, setEditableInputFields] = useState<any[]>([]);
  const [editableRoutingType, setEditableRoutingType] = useState("expression");
  const [editableBranches, setEditableBranches] = useState<any[]>([]);
  const [editableInputMappingEntries, setEditableInputMappingEntries] = useState<{ id: string; key: string; value: string }[]>([]);
  const [editableDataToDisplayEntries, setEditableDataToDisplayEntries] = useState<{ id: string; key: string; value: string }[]>([]);

  // Original values for comparison
  const [originalPrompt, setOriginalPrompt] = useState("");
  const [originalVersion, setOriginalVersion] = useState("1");
  const [originalCron, setOriginalCron] = useState("");
  const [originalOnFailure, setOriginalOnFailure] = useState("");
  const [originalTitle, setOriginalTitle] = useState("");
  const [originalTimeoutMinutes, setOriginalTimeoutMinutes] = useState("");
  const [originalOnTimeout, setOriginalOnTimeout] = useState("fail");
  const [originalInputFields, setOriginalInputFields] = useState<any[]>([]);
  const [originalRoutingType, setOriginalRoutingType] = useState("expression");
  const [originalBranches, setOriginalBranches] = useState<any[]>([]);
  const [originalInputMappingEntries, setOriginalInputMappingEntries] = useState<{ id: string; key: string; value: string }[]>([]);
  const [originalDataToDisplayEntries, setOriginalDataToDisplayEntries] = useState<{ id: string; key: string; value: string }[]>([]);

  // Track if there are unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Local state for merge strategy (for merge nodes)
  const [editableMergeStrategy, setEditableMergeStrategy] = useState('all');
  const [originalMergeStrategy, setOriginalMergeStrategy] = useState('all');
  // Local state for routing_config additional fields (Bug 4)
  const [editableDefaultBranch, setEditableDefaultBranch] = useState<any>(null);
  const [editableLlmModel, setEditableLlmModel] = useState('');
  const [editableLlmPrompt, setEditableLlmPrompt] = useState('');
  const [originalDefaultBranch, setOriginalDefaultBranch] = useState<any>(null);
  const [originalLlmModel, setOriginalLlmModel] = useState('');
  const [originalLlmPrompt, setOriginalLlmPrompt] = useState('');

  // Webhook config state
  const [webhookConfig, setWebhookConfig] = useState<any>(null);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [editableWebhookConfig, setEditableWebhookConfig] = useState<{ adapter_type: string; secrets: Record<string, string> }>({ adapter_type: '', secrets: {} });
  const [webhookSaving, setWebhookSaving] = useState(false);

  // Helpers: convert between Record<string,string> and array-of-tuples
  const entryIdCounter = useRef(0);
  const nextEntryId = () => `entry-${Date.now()}-${entryIdCounter.current++}`;
  const recordToEntries = (rec: Record<string, string>): { id: string; key: string; value: string }[] =>
    Object.entries(rec || {}).map(([key, value]) => ({ id: nextEntryId(), key, value }));
  const entriesToRecord = (entries: { id: string; key: string; value: string }[]): Record<string, string> => {
    const rec: Record<string, string> = {};
    entries.forEach(e => { rec[e.key] = e.value; });
    return rec;
  };

  // Update local state when selectedElement changes
  useEffect(() => {
    if (isNode && element.data) {
      const prompt = element.data.config.prompt || "";
      const version = element.data.config.version || "1";
      const cron = element.data.config.cron || "";
      const onFailure = element.data.config.on_failure || "";
      const title = element.data.config.title || element.data.rawData?.title || "";
      const timeoutMinutes = element.data.config.timeout_minutes || element.data.rawData?.timeout_minutes || "";
      const onTimeout = element.data.config.on_timeout || element.data.rawData?.on_timeout || "fail";
      const inputFields = element.data.config.input_fields || element.data.rawData?.input_fields || [];
      const routingType = (element.data.config.routing_config?.type || element.data.rawData?.routing_config?.type) || "expression";
      const branches = (element.data.config.routing_config?.branches || element.data.rawData?.routing_config?.branches) || [];
      const inputMappingRec = element.data.config.input_mapping || element.data.rawData?.input_mapping || {};
      const dataToDisplayRec = element.data.config.data_to_display || element.data.rawData?.data_to_display || {};
      const inputMappingArr = recordToEntries(inputMappingRec);
      const dataToDisplayArr = recordToEntries(dataToDisplayRec);
      const defaultBranch = element.data.config.routing_config?.default_branch || element.data.rawData?.routing_config?.default_branch || null;
      const llmModel = element.data.config.routing_config?.llm_model || element.data.rawData?.routing_config?.llm_model || '';
      const llmPrompt = element.data.config.routing_config?.llm_prompt || element.data.rawData?.routing_config?.llm_prompt || '';

      setEditablePrompt(prompt);
      setEditableVersion(version);
      setEditableCron(cron);
      setEditableOnFailure(onFailure);
      setEditableTitle(title);
      setEditableTimeoutMinutes(String(timeoutMinutes));
      setEditableOnTimeout(onTimeout);
      setEditableInputFields(inputFields);
      setEditableRoutingType(routingType);
      setEditableBranches(branches);
      setEditableInputMappingEntries(inputMappingArr);
      setEditableDataToDisplayEntries(dataToDisplayArr);
      setEditableDefaultBranch(defaultBranch);
      setEditableLlmModel(llmModel);
      setEditableLlmPrompt(llmPrompt);

      setOriginalPrompt(prompt);
      setOriginalVersion(version);
      setOriginalCron(cron);
      setOriginalOnFailure(onFailure);
      setOriginalTitle(title);
      setOriginalTimeoutMinutes(String(timeoutMinutes));
      setOriginalOnTimeout(onTimeout);
      setOriginalInputFields(inputFields);
      setOriginalRoutingType(routingType);
      setOriginalBranches(branches);
      setOriginalInputMappingEntries(inputMappingArr);
      setOriginalDataToDisplayEntries(dataToDisplayArr);
      setOriginalDefaultBranch(defaultBranch);
      setOriginalLlmModel(llmModel);
      setOriginalLlmPrompt(llmPrompt);

      // For merge node, initialize merge strategy state
      if (element.data.type === 'merge') {
        const ms = element.data.config.merge_strategy || 'all';
        setEditableMergeStrategy(ms);
        setOriginalMergeStrategy(ms);
      }
      setHasUnsavedChanges(false);
    }
  }, [selectedElement, isNode, element, readonly]);

  // Fetch webhook config for trigger nodes
  useEffect(() => {
    if (element?.data?.type === 'trigger' && element?.data?.config?.triggerType === 'webhook') {
      if (workflowId) {
        // Existing workflow — fetch from API
        setWebhookLoading(true);
        getWorkflowWebhookConfig(workflowId)
          .then((config: any) => {
            setWebhookConfig(config);
            // Populate editable state from API response for edit mode
            if (config && config.adapter_type) {
              setEditableWebhookConfig({
                adapter_type: config.adapter_type,
                secrets: config.secrets_ref
                  ? Object.fromEntries(
                      Object.entries(config.secrets_ref).map(([k, v]: [string, any]) =>
                        [k, typeof v === 'object' && v?.key_id ? `key:${v.key_id}` : '']
                      )
                    )
                  : {},
              });
            }
          })
          .catch(() => setWebhookConfig(null))
          .finally(() => setWebhookLoading(false));
      } else {
        // Create mode — read from node data
        const nodeWebhookConfig = element.data.config?.webhookConfig;
        if (nodeWebhookConfig) {
          setEditableWebhookConfig({
            adapter_type: nodeWebhookConfig.adapter_type || '',
            secrets: nodeWebhookConfig.secrets || {},
          });
          setWebhookConfig({ adapter_type: nodeWebhookConfig.adapter_type, enabled: false, from_node: true });
        }
      }
    } else {
      setWebhookConfig(null);
    }
  }, [element, workflowId]);

  // Check for changes
  useEffect(() => {
    if (readonly) {
      setHasUnsavedChanges(false);
      return;
    }
    if (!element) {
      setHasUnsavedChanges(false);
      return;
    }
    const promptChanged = editablePrompt !== originalPrompt;
    const versionChanged = editableVersion !== originalVersion;
    const cronChanged = editableCron !== originalCron;
    const onFailureChanged = editableOnFailure !== originalOnFailure;
    const titleChanged = editableTitle !== originalTitle;
    const timeoutMinutesChanged = editableTimeoutMinutes !== originalTimeoutMinutes;
    const onTimeoutChanged = editableOnTimeout !== originalOnTimeout;
    const inputFieldsChanged = JSON.stringify(editableInputFields) !== JSON.stringify(originalInputFields);
    const routingTypeChanged = editableRoutingType !== originalRoutingType;
    const branchesChanged = JSON.stringify(editableBranches) !== JSON.stringify(originalBranches);
    const defaultBranchChanged = editableDefaultBranch !== originalDefaultBranch;
    const llmModelChanged = editableLlmModel !== originalLlmModel;
    const llmPromptChanged = editableLlmPrompt !== originalLlmPrompt;
    // For merge node, check merge strategy
    let mergeStrategyChanged = false;
    if (isNode && element.data && element.data.type === 'merge') {
      mergeStrategyChanged = editableMergeStrategy !== originalMergeStrategy;
    }
    // Check if input mapping changed
    const inputMappingChanged = JSON.stringify(editableInputMappingEntries.map(e => [e.key, e.value]))
      !== JSON.stringify(originalInputMappingEntries.map(e => [e.key, e.value]));
    // Check if data_to_display changed
    const dataToDisplayChanged = JSON.stringify(editableDataToDisplayEntries.map(e => [e.key, e.value]))
      !== JSON.stringify(originalDataToDisplayEntries.map(e => [e.key, e.value]));
    setHasUnsavedChanges(
      promptChanged || versionChanged || cronChanged || onFailureChanged || mergeStrategyChanged ||
      titleChanged || timeoutMinutesChanged || onTimeoutChanged || inputFieldsChanged ||
      routingTypeChanged || branchesChanged || inputMappingChanged || dataToDisplayChanged ||
      defaultBranchChanged || llmModelChanged || llmPromptChanged
    );
  }, [
    editablePrompt,
    editableVersion,
    editableCron,
    editableOnFailure,
    editableTitle,
    editableTimeoutMinutes,
    editableOnTimeout,
    editableInputFields,
    editableRoutingType,
    editableBranches,
    editableInputMappingEntries,
    editableDataToDisplayEntries,
    editableDefaultBranch,
    editableLlmModel,
    editableLlmPrompt,
    originalPrompt,
    originalVersion,
    originalCron,
    originalOnFailure,
    originalTitle,
    originalTimeoutMinutes,
    originalOnTimeout,
    originalInputFields,
    originalRoutingType,
    originalBranches,
    originalInputMappingEntries,
    originalDataToDisplayEntries,
    originalDefaultBranch,
    originalLlmModel,
    originalLlmPrompt,
    editableMergeStrategy,
    originalMergeStrategy,
    readonly,
    isNode,
    element
  ]);

  // Handle save - commit changes to parent
  const handleSave = () => {
    if (isNode && element.data) {
      // For schedule triggers, update the label based on the cron expression
      const isScheduleTrigger =
        element.data.type === "trigger" &&
        element.data.config.triggerType === "schedule";
      const updatedLabel =
        isScheduleTrigger && editableCron
          ? getScheduleLabel(editableCron)
          : element.data.label;

      // For merge node, update merge_strategy
      let configUpdate = {
        ...element.data.config,
        prompt: editablePrompt,
        version: editableVersion,
        cron: editableCron,
        on_failure: editableOnFailure || undefined,
      };
      if (element.data.type === 'merge') {
        configUpdate = {
          ...configUpdate,
          merge_strategy: editableMergeStrategy,
        };
      }
      if (element.data.type === 'human_input') {
        configUpdate = {
          ...configUpdate,
          title: editableTitle,
          timeout_minutes: editableTimeoutMinutes ? parseInt(editableTimeoutMinutes) : undefined,
          on_timeout: editableOnTimeout,
          input_fields: editableInputFields,
          data_to_display: entriesToRecord(editableDataToDisplayEntries),
        };
      }
      if (element.data.type === 'conditional') {
        configUpdate = {
          ...configUpdate,
          routing_config: {
            type: editableRoutingType,
            branches: editableBranches,
            ...(editableDefaultBranch ? { default_branch: editableDefaultBranch } : {}),
            ...(editableLlmModel ? { llm_model: editableLlmModel } : {}),
            ...(editableLlmPrompt ? { llm_prompt: editableLlmPrompt } : {}),
          },
        };
      }
      if (element.data.type === 'agent' || element.data.type === 'tool' || element.data.type === 'conditional') {
        configUpdate = {
          ...configUpdate,
          input_mapping: entriesToRecord(editableInputMappingEntries),
        };
      }

      let rawDataUpdate = element.data.rawData || {};
      if (element.data.type === 'human_input') {
        rawDataUpdate = {
          ...rawDataUpdate,
          title: editableTitle,
          timeout_minutes: editableTimeoutMinutes ? parseInt(editableTimeoutMinutes) : undefined,
          on_timeout: editableOnTimeout,
          input_fields: editableInputFields,
          data_to_display: entriesToRecord(editableDataToDisplayEntries),
        };
      }
      if (element.data.type === 'conditional') {
        rawDataUpdate = {
          ...rawDataUpdate,
          routing_config: {
            type: editableRoutingType,
            branches: editableBranches,
            ...(editableDefaultBranch ? { default_branch: editableDefaultBranch } : {}),
            ...(editableLlmModel ? { llm_model: editableLlmModel } : {}),
            ...(editableLlmPrompt ? { llm_prompt: editableLlmPrompt } : {}),
          },
          input_mapping: entriesToRecord(editableInputMappingEntries),
        };
      }
      // Bug 1 fix: Update rawData for agent/tool nodes
      if (element.data.type === 'agent' || element.data.type === 'tool') {
        rawDataUpdate = {
          ...rawDataUpdate,
          prompt: editablePrompt,
          input_mapping: entriesToRecord(editableInputMappingEntries),
        };
      }
      // Bug 1 fix: Update rawData for merge nodes
      if (element.data.type === 'merge') {
        rawDataUpdate = {
          ...rawDataUpdate,
          merge_strategy: editableMergeStrategy,
        };
      }

      if (onUpdateNode) {
        onUpdateNode(element.id, {
          ...element.data,
          label: updatedLabel,
          config: configUpdate,
          rawData: rawDataUpdate,
        });
      }

      // Update original values
      setOriginalPrompt(editablePrompt);
      setOriginalVersion(editableVersion);
      setOriginalCron(editableCron);
      setOriginalOnFailure(editableOnFailure);
      setOriginalTitle(editableTitle);
      setOriginalTimeoutMinutes(editableTimeoutMinutes);
      setOriginalOnTimeout(editableOnTimeout);
      setOriginalInputFields(editableInputFields);
      setOriginalRoutingType(editableRoutingType);
      setOriginalBranches(editableBranches);
      setOriginalInputMappingEntries(editableInputMappingEntries);
      setOriginalDataToDisplayEntries(editableDataToDisplayEntries);
      setOriginalDefaultBranch(editableDefaultBranch);
      setOriginalLlmModel(editableLlmModel);
      setOriginalLlmPrompt(editableLlmPrompt);
      if (element.data.type === 'merge') {
        setOriginalMergeStrategy(editableMergeStrategy);
      }
      setHasUnsavedChanges(false);
    }
  };

  // Handle cancel - revert to original values
  const handleCancel = () => {
    setEditablePrompt(originalPrompt);
    setEditableVersion(originalVersion);
    setEditableCron(originalCron);
    setEditableOnFailure(originalOnFailure);
    setEditableTitle(originalTitle);
    setEditableTimeoutMinutes(originalTimeoutMinutes);
    setEditableOnTimeout(originalOnTimeout);
    setEditableInputFields(originalInputFields);
    setEditableRoutingType(originalRoutingType);
    setEditableBranches(originalBranches);
    setEditableInputMappingEntries(originalInputMappingEntries);
    setEditableDataToDisplayEntries(originalDataToDisplayEntries);
    setEditableDefaultBranch(originalDefaultBranch);
    setEditableLlmModel(originalLlmModel);
    setEditableLlmPrompt(originalLlmPrompt);
    if (isNode && element.data && element.data.type === 'merge') {
      setEditableMergeStrategy(originalMergeStrategy);
    }
    setHasUnsavedChanges(false);
  };

  // Handle close - warn if unsaved changes
  const handleClose = () => {
    if (readonly) {
      onClose();
      return;
    }
    if (hasUnsavedChanges) {
      if (window.confirm("You have unsaved changes. Close without saving?")) {
        handleCancel(); // Revert changes
        onClose();
      }
    } else {
      onClose();
    }
  };

  // Get icon and color based on node type
  const getNodeIcon = (type: string, triggerType?: string) => {
    if (type === "agent") return SmartToyIcon;
    if (type === "tool") return BuildIcon;
    if (type === "conditional") return AltRouteIcon;
    if (type === "human_input") return PersonOutlineIcon;
    if (type === "loop") return LoopIcon;
    if (type === "merge") return CallMergeIcon;
    if (type === "wait") return HourglassEmptyIcon;
    if (type === "trigger") {
      return triggerType === "schedule" ? ScheduleIcon : CloudQueueIcon;
    }
    return SmartToyIcon;
  };

  const getNodeColor = (type: string, triggerType?: string) => {
    if (type === "agent") return "#B87333";
    if (type === "tool") return "#789769";
    if (type === "conditional") return "#D4A017";
    if (type === "human_input") return "#8E588E";
    if (type === "loop") return "#4A90D9";
    if (type === "merge") return "#2E8B57";
    if (type === "wait") return "#6B7B8D";
    if (type === "trigger") {
      return triggerType === "schedule" ? "#5A7D9A" : "#2C3E50";
    }
    return "#64748B";
  };

  const getNodeTypeLabel = (type: string, triggerType?: string) => {
    if (type === "trigger") {
      return triggerType === "schedule"
        ? "Schedule Trigger"
        : "Webhook Trigger";
    }
    const labels: Record<string, string> = {
      agent: "Agent",
      tool: "Tool",
      conditional: "Conditional",
      human_input: "Human Approval",
      loop: "Loop",
      merge: "Merge",
      wait: "Wait",
    };
    return labels[type] || type.charAt(0).toUpperCase() + type.slice(1);
  };

  const IconComponent =
    isNode && element.data?.type
      ? getNodeIcon(element.data.type, element.data.config?.triggerType)
      : ArrowForwardIcon;

  const iconColor =
    isNode && element.data?.type
      ? getNodeColor(element.data.type, element.data.config?.triggerType)
      : "#64748B";

  const typeLabel =
    isNode && element.data?.type
      ? getNodeTypeLabel(element.data.type, element.data.config?.triggerType)
      : "Connection";

  // Compute the current display label (updates dynamically with edits)
  const displayLabel = 
    isNode && element.data?.type === "trigger" && element.data.config?.triggerType === "schedule" && editableCron
      ? getScheduleLabel(editableCron)
      : (isNode ? element.data.label : "");

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      variant="temporary"
      sx={{
        zIndex: 1300,
        "& .MuiDrawer-paper": { width: "30%", boxSizing: "border-box" },
      }}
    >
      {/* Header Bar with Icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          bgcolor: "#F8FAFC",
          borderBottom: "1px solid #E2E8F0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: iconColor,
              color: "#FFFFFF",
              flexShrink: 0,
            }}
          >
            <IconComponent sx={{ fontSize: 20 }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: "1rem",
              color: "#0F172A",
            }}
          >
            {typeLabel}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3, pb: hasUnsavedChanges && isNode ? 20 : 3 }}>
        {!element ? (
          <Typography
            sx={{
              fontSize: "13px",
              color: "#64748B",
            }}
          >
            No element selected
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {isNode ? (
              // Node Details
              <>
                {/* Available Outputs from Predecessor Nodes */}
                {availableOutputs.length > 0 && (
                  <Accordion
                    defaultExpanded
                    elevation={0}
                    sx={{
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px !important',
                      '&:before': { display: 'none' },
                      bgcolor: '#FAFBFC',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        bgcolor: '#F8FAFC',
                        borderRadius: '8px 8px 0 0',
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          gap: 1,
                        },
                      }}
                    >
                      <InputIcon sx={{ fontSize: 18, color: '#B87333' }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1E293B' }}>
                        Available Inputs ({availableOutputs.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2, bgcolor: '#FFFFFF' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {availableOutputs.map((output, idx) => (
                          <Paper
                            key={idx}
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: '6px',
                              border: '1px solid #E2E8F0',
                              bgcolor: '#F8FAFC',
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: '#B87333',
                                bgcolor: '#FFF7F0',
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <CodeIcon sx={{ fontSize: 12, color: '#B87333' }} />
                              <Typography sx={{ fontWeight: 700, fontSize: '11px', fontFamily: '"IBM Plex Mono", monospace', color: '#1E293B' }}>
                                {output.name}
                              </Typography>
                              <Chip
                                label={output.type}
                                size="small"
                                sx={{
                                  height: '16px',
                                  fontSize: '9px',
                                  fontWeight: 700,
                                  bgcolor: '#E0F2FE',
                                  color: '#0369A1',
                                }}
                              />
                            </Box>
                            <Typography sx={{ fontSize: '10px', color: '#64748B', mb: 0.75 }}>
                              {output.description}
                            </Typography>
                            <Box sx={{ p: 0.75, borderRadius: '4px', bgcolor: '#F1F5F9', border: '1px solid #E2E8F0' }}>
                              <Typography sx={{ fontSize: '9px', fontFamily: '"IBM Plex Mono", monospace', color: '#475569' }}>
                                {output.jsonPath}
                              </Typography>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Show Agent/Tool ID at the top for agent/tool nodes */}
                {(element.data.type === "agent" || element.data.type === "tool") && element.data.config.id && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#64748B",
                        fontSize: "12px",
                        display: "block",
                        mb: 0.75,
                      }}
                    >
                      {element.data.type === "agent" ? "Agent ID" : "Tool ID"}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        bgcolor: "#F8FAFC",
                        p: 1.25,
                        borderRadius: "6px",
                        fontSize: "13px",
                        color: "#334155",
                        border: "1px solid #E2E8F0",
                        wordBreak: "break-all",
                      }}
                    >
                      {element.data.config.id}
                    </Typography>
                  </Box>
                )}

                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "#64748B",
                      fontSize: "12px",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    Label
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#0F172A",
                    }}
                  >
                    {displayLabel}
                  </Typography>
                </Box>

                {/* Step ID - Show for all node types */}
                {isNode && element.id && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#64748B",
                        fontSize: "12px",
                        display: "block",
                        mb: 0.75,
                      }}
                    >
                      Step ID
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: '#F8FAFC',
                        p: 1.25,
                        borderRadius: '6px',
                        border: '1px solid #E2E8F0',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '13px',
                          color: '#334155',
                          wordBreak: 'break-all',
                          flex: 1,
                        }}
                      >
                        {element.id}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          navigator.clipboard.writeText(element.id);
                        }}
                        sx={{
                          color: '#B87333',
                          '&:hover': { bgcolor: '#FFF7F0' },
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {(element.data.type === "agent" || element.data.type === "tool") && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#64748B",
                        fontSize: "12px",
                        display: "block",
                        mb: 0.75,
                      }}
                    >
                      Version
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={editableVersion}
                      disabled={readonly}
                      onChange={(e) => setEditableVersion(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "13px",
                          bgcolor: "#FFFFFF",
                        },
                        "& .MuiOutlinedInput-input": {
                          py: 1,
                        },
                      }}
                    />
                  </Box>
                )}

                {(element.data.type === "agent" || element.data.type === "tool") && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#64748B",
                        fontSize: "12px",
                        display: "block",
                        mb: 0.75,
                      }}
                    >
                      Timeout (seconds)
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={element.data.config.timeout_seconds || element.data.rawData?.timeout_seconds || ''}
                      disabled={readonly}
                      onChange={(e) => {
                        const timeoutValue = parseInt(e.target.value) || undefined;
                        if (onUpdateNode) {
                          onUpdateNode(element.id, {
                            ...element.data,
                            config: { ...element.data.config, timeout_seconds: timeoutValue },
                            rawData: { ...element.data.rawData, timeout_seconds: timeoutValue }
                          });
                        }
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "13px",
                          bgcolor: "#FFFFFF",
                        },
                        "& .MuiOutlinedInput-input": {
                          py: 1,
                        },
                      }}
                      inputProps={{ min: 1 }}
                    />
                  </Box>
                )}

                {element.data.type === "trigger" && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#64748B",
                        fontSize: "12px",
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      Trigger Type
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#334155",
                        textTransform: "capitalize",
                      }}
                    >
                      {element.data.config.triggerType}
                    </Typography>
                  </Box>
                )}

                {element.data.type === "trigger" &&
                  element.data.config.triggerType === "schedule" && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: "#64748B",
                          fontSize: "12px",
                          display: "block",
                          mb: 1.5,
                        }}
                      >
                        Schedule Configuration
                      </Typography>
                      <CronScheduleBuilder
                        value={editableCron}
                        onChange={setEditableCron}
                        showValidation={true}
                        readonly={readonly}
                      />
                    </Box>
                  )}

                {element.data.type === "trigger" &&
                  element.data.config.triggerType === "webhook" && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: "#64748B",
                          fontSize: "12px",
                          display: "block",
                          mb: 1,
                        }}
                      >
                        Webhook Configuration
                      </Typography>
                      {webhookLoading ? (
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "11px", color: "#94A3B8" }}
                        >
                          Loading...
                        </Typography>
                      ) : readonly ? (
                        /* View mode: static display */
                        webhookConfig && webhookConfig.adapter_type ? (
                          <Box
                            sx={{
                              p: 1.5,
                              bgcolor: "#F8FAFC",
                              borderRadius: "6px",
                              border: "1px solid #E2E8F0",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                              <Chip
                                label={webhookConfig.adapter_type || "webhook"}
                                size="small"
                                sx={{ fontSize: "10px", fontWeight: 600 }}
                              />
                              {webhookConfig.enabled !== undefined && (
                                <Chip
                                  label={webhookConfig.enabled ? "Enabled" : "Disabled"}
                                  size="small"
                                  color={webhookConfig.enabled ? "success" : "default"}
                                  sx={{ fontSize: "10px" }}
                                />
                              )}
                            </Box>
                            {webhookConfig.secrets_ref &&
                              Object.entries(webhookConfig.secrets_ref).map(
                                ([name, ref]: [string, any]) => (
                                  <Typography
                                    key={name}
                                    variant="caption"
                                    sx={{
                                      fontSize: "11px",
                                      color: "#64748B",
                                      display: "block",
                                    }}
                                  >
                                    {name}:{" "}
                                    {typeof ref === "object" && ref?.key_name
                                      ? ref.key_name
                                      : "***"}
                                  </Typography>
                                )
                              )}
                          </Box>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "11px", color: "#94A3B8" }}
                          >
                            No webhook configured
                          </Typography>
                        )
                      ) : (
                        /* Edit/Create mode: editable WebhookConfigBuilder */
                        <Box>
                          <WebhookConfigBuilder
                            value={editableWebhookConfig}
                            onChange={(newConfig) => {
                              setEditableWebhookConfig(newConfig);
                              // In create mode (no workflowId), update node data directly
                              if (!workflowId && onUpdateNode && element) {
                                onUpdateNode(element.id, {
                                  config: {
                                    ...element.data.config,
                                    webhookConfig: newConfig,
                                  },
                                });
                              }
                            }}
                            showValidation={false}
                          />
                          {workflowId && (
                            <Button
                              variant="contained"
                              size="small"
                              disabled={webhookSaving || !editableWebhookConfig.adapter_type}
                              onClick={async () => {
                                setWebhookSaving(true);
                                try {
                                  await configureWorkflowWebhook(workflowId, {
                                    enabled: true,
                                    adapter_type: editableWebhookConfig.adapter_type,
                                    secrets_ref: editableWebhookConfig.secrets,
                                  });
                                  // Refresh config display
                                  const updated = await getWorkflowWebhookConfig(workflowId);
                                  setWebhookConfig(updated);
                                } catch (e) {
                                  console.error("Failed to save webhook config:", e);
                                } finally {
                                  setWebhookSaving(false);
                                }
                              }}
                              sx={{ mt: 1.5, fontSize: "11px", textTransform: "none" }}
                              startIcon={<SaveIcon sx={{ fontSize: "14px" }} />}
                            >
                              {webhookSaving ? "Saving..." : "Save Webhook Config"}
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}

                {element.data.type !== "trigger" && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#64748B",
                        fontSize: "12px",
                        display: "block",
                        mb: 0.75,
                      }}
                    >
                      On Failure
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={editableOnFailure}
                      disabled={readonly}
                      onChange={(e) => setEditableOnFailure(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "13px",
                          bgcolor: "#FFFFFF",
                        },
                      }}
                    >
                      <MenuItem value="">Default (fail)</MenuItem>
                      <MenuItem value="fail">Fail — stop workflow</MenuItem>
                      <MenuItem value="skip">Skip — continue workflow</MenuItem>
                    </TextField>
                  </Box>
                )}

                {element.data.type !== "trigger" && (element.data.type === "agent" || element.data.type === "human_input") && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#64748B",
                        fontSize: "12px",
                        display: "block",
                        mb: 0.75,
                      }}
                    >
                      Prompt
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      size="small"
                      value={editablePrompt}
                      disabled={readonly}
                      onChange={(e) => setEditablePrompt(e.target.value)}
                      placeholder="Enter prompt..."
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "13px",
                          bgcolor: "#FFFFFF",
                          fontFamily: "inherit",
                        },
                        "& .MuiOutlinedInput-input": {
                          lineHeight: 1.6,
                        },
                      }}
                    />
                  </Box>
                )}

                {/* Conditional Step Properties */}
                {element.data.type === "conditional" && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', fontSize: '12px', display: 'block', mb: 0.75 }}>
                      Routing Type
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={editableRoutingType}
                      disabled={readonly}
                      onChange={(e) => {
                        setEditableRoutingType(e.target.value);
                      }}
                      sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { fontSize: '13px', bgcolor: '#FFFFFF' } }}
                    >
                      <MenuItem value="expression">Expression</MenuItem>
                      <MenuItem value="llm">LLM</MenuItem>
                    </TextField>

                    {editableRoutingType === 'llm' && (
                      <>
                        <TextField
                          fullWidth
                          size="small"
                          label="LLM Model"
                          value={editableLlmModel}
                          disabled={readonly}
                          onChange={(e) => setEditableLlmModel(e.target.value)}
                          placeholder="e.g. gpt-4o"
                          sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { fontSize: '13px', bgcolor: '#FFFFFF' } }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="LLM Prompt"
                          value={editableLlmPrompt}
                          disabled={readonly}
                          onChange={(e) => setEditableLlmPrompt(e.target.value)}
                          multiline
                          rows={2}
                          placeholder="How should the LLM choose a branch..."
                          sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { fontSize: '13px', bgcolor: '#FFFFFF' } }}
                        />
                      </>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', fontSize: '12px' }}>
                        Branches ({editableBranches.length})
                      </Typography>
                      {!readonly && (
                        <Button
                          size="small"
                          onClick={() => {
                            const newBranch = { id: '', condition: '', next_steps: [] };
                            setEditableBranches([...editableBranches, newBranch]);
                          }}
                          sx={{ textTransform: 'none', fontSize: '11px', fontWeight: 700 }}
                        >
                          + Add Branch
                        </Button>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {editableBranches.map((branch: any, idx: number) => (
                        <Paper key={idx} elevation={0} sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Branch ID"
                              value={branch.id || ''}
                              disabled={readonly}
                              onChange={(e) => {
                                const branches = [...editableBranches];
                                branches[idx] = { ...branches[idx], id: e.target.value };
                                setEditableBranches(branches);
                              }}
                              sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', bgcolor: '#FFFFFF' } }}
                            />
                            {!readonly && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const branches = [...editableBranches];
                                  branches.splice(idx, 1);
                                  setEditableBranches(branches);
                                }}
                                sx={{ color: '#EF4444', mt: 0.5 }}
                              >
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </Box>
                          <TextField
                            fullWidth
                            size="small"
                            label="Condition"
                            value={branch.condition || ''}
                            disabled={readonly}
                            onChange={(e) => {
                              const branches = [...editableBranches];
                              branches[idx] = { ...branches[idx], condition: e.target.value };
                              setEditableBranches(branches);
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', bgcolor: '#FFFFFF' } }}
                          />
                          <Autocomplete
                            multiple
                            size="small"
                            options={nodes.filter(n => n.data.type !== 'trigger' && n.id !== element.id).map(n => n.id)}
                            getOptionLabel={(option) => {
                              const node = nodes.find(n => n.id === option);
                              const label = node?.data.label || option;
                              return `${label} (${option})`;
                            }}
                            isOptionEqualToValue={(option, value) => option === value}
                            renderOption={(props, option) => {
                              const node = nodes.find(n => n.id === option);
                              return (
                                <li {...props} key={option}>
                                  <Box>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                                      {node?.data.label || option}
                                    </Typography>
                                    <Typography sx={{ fontSize: '10px', color: '#64748B', fontFamily: 'monospace' }}>
                                      {option}
                                    </Typography>
                                  </Box>
                                </li>
                              );
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, i) => {
                                const node = nodes.find(n => n.id === option);
                                return (
                                  <Chip
                                    {...getTagProps({ index: i })}
                                    key={option}
                                    label={node?.data.label || option}
                                    size="small"
                                    title={option}
                                    sx={{ fontSize: '11px' }}
                                  />
                                );
                              })
                            }
                            value={branch.next_steps || []}
                            disabled={readonly}
                            onChange={(_e, newValue) => {
                              const branches = [...editableBranches];
                              branches[idx] = { ...branches[idx], next_steps: newValue };
                              setEditableBranches(branches);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Next Steps"
                                placeholder="Select downstream steps..."
                                sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', bgcolor: '#FFFFFF' } }}
                              />
                            )}
                            sx={{ mt: 1 }}
                          />
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Human Input Step Properties */}
                {element.data.type === "human_input" && (
                  <Box>
                    {/* Title */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', fontSize: '12px', display: 'block', mb: 0.75 }}>
                        Title
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={editableTitle}
                        disabled={readonly}
                        onChange={(e) => setEditableTitle(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px', bgcolor: '#FFFFFF' } }}
                      />
                    </Box>

                    {/* Timeout Minutes */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', fontSize: '12px', display: 'block', mb: 0.75 }}>
                        Timeout (minutes)
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={editableTimeoutMinutes}
                        disabled={readonly}
                        onChange={(e) => setEditableTimeoutMinutes(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px', bgcolor: '#FFFFFF' } }}
                        inputProps={{ min: 1 }}
                      />
                    </Box>

                    {/* On Timeout */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', fontSize: '12px', display: 'block', mb: 0.75 }}>
                        On Timeout
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={editableOnTimeout}
                        disabled={readonly}
                        onChange={(e) => setEditableOnTimeout(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px', bgcolor: '#FFFFFF' } }}
                      >
                        <MenuItem value="fail">Fail</MenuItem>
                        <MenuItem value="skip">Skip</MenuItem>
                      </TextField>
                    </Box>

                    {/* Data to Display */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', fontSize: '12px' }}>
                          Data to Display ({editableDataToDisplayEntries.length})
                        </Typography>
                        {!readonly && (
                          <Button
                            size="small"
                            onClick={() => {
                              const newKey = `field_${editableDataToDisplayEntries.length + 1}`;
                              setEditableDataToDisplayEntries([...editableDataToDisplayEntries, { id: nextEntryId(), key: newKey, value: '' }]);
                            }}
                            sx={{ textTransform: 'none', fontSize: '11px', fontWeight: 700 }}
                          >
                            + Add Display Field
                          </Button>
                        )}
                      </Box>

                      <Typography sx={{ fontSize: '10px', color: '#64748B', mb: 1.5 }}>
                        Define what data to show to the reviewer. Use JSONPath expressions to reference outputs from previous steps.
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {editableDataToDisplayEntries.map((entry) => (
                          <Paper key={entry.id} elevation={0} sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Display Field Name"
                                value={entry.key}
                                disabled={readonly}
                                onChange={(e) => {
                                  setEditableDataToDisplayEntries(prev =>
                                    prev.map(item => item.id === entry.id ? { ...item, key: e.target.value } : item)
                                  );
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', bgcolor: '#FFFFFF' } }}
                                placeholder="e.g. campaign_plan, research_report"
                              />
                              {!readonly && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditableDataToDisplayEntries(prev => prev.filter(item => item.id !== entry.id));
                                  }}
                                  sx={{ color: '#EF4444' }}
                                >
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                            </Box>

                            <TextField
                              fullWidth
                              size="small"
                              label="JSONPath Expression"
                              value={entry.value}
                              disabled={readonly}
                              onChange={(e) => {
                                setEditableDataToDisplayEntries(prev =>
                                  prev.map(item => item.id === entry.id ? { ...item, value: e.target.value } : item)
                                );
                              }}
                              sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', bgcolor: '#FFFFFF', fontFamily: '"IBM Plex Mono", monospace' } }}
                              placeholder="$.steps.[step_id].output"
                              helperText="Use Available Inputs above to find the correct JSONPath"
                              FormHelperTextProps={{ sx: { fontSize: '9px', mt: 0.5 } }}
                            />
                          </Paper>
                        ))}

                        {editableDataToDisplayEntries.length === 0 && (
                          <Typography sx={{ fontSize: '12px', color: '#94A3B8', fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                            No display fields yet. Click "+ Add Display Field" to show data to the reviewer.
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Input Fields - Now Editable */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', fontSize: '12px' }}>
                          Input Fields ({editableInputFields.length})
                        </Typography>
                        {!readonly && (
                          <Button
                            size="small"
                            onClick={() => {
                              const newField = { name: '', label: '', type: 'text', required: true };
                              setEditableInputFields([...editableInputFields, newField]);
                            }}
                            sx={{ textTransform: 'none', fontSize: '11px', fontWeight: 700 }}
                          >
                            + Add Field
                          </Button>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {editableInputFields.map((field: any, fieldIdx: number) => (
                          <Paper key={fieldIdx} elevation={0} sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Field Name"
                                value={field.name || ''}
                                disabled={readonly}
                                onChange={(e) => {
                                  const fields = [...editableInputFields];
                                  fields[fieldIdx] = { ...fields[fieldIdx], name: e.target.value };
                                  setEditableInputFields(fields);
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', bgcolor: '#FFFFFF' } }}
                              />
                              {!readonly && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const fields = [...editableInputFields];
                                    fields.splice(fieldIdx, 1);
                                    setEditableInputFields(fields);
                                  }}
                                  sx={{ color: '#EF4444' }}
                                >
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                            </Box>

                            <TextField
                              fullWidth
                              size="small"
                              label="Label"
                              value={field.label || ''}
                              disabled={readonly}
                              onChange={(e) => {
                                const fields = [...editableInputFields];
                                fields[fieldIdx] = { ...fields[fieldIdx], label: e.target.value };
                                setEditableInputFields(fields);
                              }}
                              sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '12px', bgcolor: '#FFFFFF' } }}
                            />

                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Type"
                              value={field.type || 'text'}
                              disabled={readonly}
                              onChange={(e) => {
                                const fields = [...editableInputFields];
                                fields[fieldIdx] = { ...fields[fieldIdx], type: e.target.value };
                                setEditableInputFields(fields);
                              }}
                              sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '12px', bgcolor: '#FFFFFF' } }}
                            >
                              <MenuItem value="text">Text</MenuItem>
                              <MenuItem value="textarea">Textarea</MenuItem>
                              <MenuItem value="select">Select</MenuItem>
                              <MenuItem value="boolean">Boolean</MenuItem>
                            </TextField>

                            {field.type === 'select' && (
                              <TextField
                                fullWidth
                                size="small"
                                label="Options (comma-separated)"
                                value={Array.isArray(field.options) ? field.options.join(', ') : String(field.options || '')}
                                disabled={readonly}
                                onChange={(e) => {
                                  const fields = [...editableInputFields];
                                  fields[fieldIdx] = { ...fields[fieldIdx], options: e.target.value };
                                  setEditableInputFields(fields);
                                }}
                                onBlur={(e) => {
                                  const fields = [...editableInputFields];
                                  const optionsArray = e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean);
                                  fields[fieldIdx] = { ...fields[fieldIdx], options: optionsArray };
                                  setEditableInputFields(fields);
                                }}
                                sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '12px', bgcolor: '#FFFFFF' } }}
                                placeholder="e.g. Approve, Revise, Reject"
                              />
                            )}

                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Required"
                              value={field.required !== undefined ? String(field.required) : 'true'}
                              disabled={readonly}
                              onChange={(e) => {
                                const fields = [...editableInputFields];
                                fields[fieldIdx] = { ...fields[fieldIdx], required: e.target.value === 'true' };
                                setEditableInputFields(fields);
                              }}
                              sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', bgcolor: '#FFFFFF' } }}
                            >
                              <MenuItem value="true">Yes</MenuItem>
                              <MenuItem value="false">No</MenuItem>
                            </TextField>
                          </Paper>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Loop Step Properties */}
                {element.data.type === "loop" && element.data.config.loop_config && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', fontSize: '12px', display: 'block', mb: 0.75 }}>
                      Loop Type
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={element.data.config.loop_config.type || 'for_each'}
                      disabled={readonly}
                      onChange={e => onUpdateNode && onUpdateNode(element.id, { ...element.data, config: { ...element.data.config, loop_config: { ...element.data.config.loop_config, type: e.target.value } } })}
                      sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '13px', bgcolor: '#FFFFFF' } }}
                    >
                      <MenuItem value="for_each">For Each</MenuItem>
                      <MenuItem value="while">While</MenuItem>
                    </TextField>
                    <TextField
                      fullWidth
                      size="small"
                      label="Collection (JSONPath)"
                      value={element.data.config.loop_config.collection || ''}
                      disabled={readonly}
                      onChange={e => onUpdateNode && onUpdateNode(element.id, { ...element.data, config: { ...element.data.config, loop_config: { ...element.data.config.loop_config, collection: e.target.value } } })}
                      sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '13px', bgcolor: '#FFFFFF' } }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="While Condition"
                      value={element.data.config.loop_config.condition || ''}
                      disabled={readonly}
                      onChange={e => onUpdateNode && onUpdateNode(element.id, { ...element.data, config: { ...element.data.config, loop_config: { ...element.data.config.loop_config, condition: e.target.value } } })}
                      sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '13px', bgcolor: '#FFFFFF' } }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Max Iterations"
                      type="number"
                      value={element.data.config.loop_config.max_iterations || ''}
                      disabled={readonly}
                      onChange={e => onUpdateNode && onUpdateNode(element.id, { ...element.data, config: { ...element.data.config, loop_config: { ...element.data.config.loop_config, max_iterations: parseInt(e.target.value) || undefined } } })}
                      sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '13px', bgcolor: '#FFFFFF' } }}
                    />
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Output Mode"
                      value={element.data.config.output_mode || 'collect'}
                      disabled={readonly}
                      onChange={e => onUpdateNode && onUpdateNode(element.id, { ...element.data, config: { ...element.data.config, output_mode: e.target.value } })}
                      sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '13px', bgcolor: '#FFFFFF' } }}
                    >
                      <MenuItem value="collect">Collect all</MenuItem>
                      <MenuItem value="last">Last only</MenuItem>
                    </TextField>
                  </Box>
                )}

                {/* Merge Step Properties */}
                {element.data.type === "merge" && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', fontSize: '12px', display: 'block', mb: 0.75 }}>
                      Merge Strategy
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={editableMergeStrategy}
                      disabled={readonly}
                      onChange={e => setEditableMergeStrategy(e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px', bgcolor: '#FFFFFF' } }}
                    >
                      <MenuItem value="all">All (require all inputs)</MenuItem>
                      <MenuItem value="all_available">All Available (merge whatever is present)</MenuItem>
                      <MenuItem value="array">Array (ordered aggregation)</MenuItem>
                      <MenuItem value="first_available">First Available</MenuItem>
                    </TextField>
                  </Box>
                )}

                {/* Wait Step Properties */}
                {element.data.type === "wait" && element.data.config.wait_config && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', fontSize: '12px', display: 'block', mb: 0.75 }}>
                      Wait Type
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value="duration"
                      disabled={true}
                      onChange={() => {}}
                      sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '13px', bgcolor: '#FFFFFF' } }}
                    >
                      <MenuItem value="duration">Duration</MenuItem>
                    </TextField>
                    <TextField
                      fullWidth
                      size="small"
                      label="Duration (seconds)"
                      type="number"
                      value={element.data.config.wait_config.duration_seconds || ''}
                      disabled={readonly}
                      onChange={e => onUpdateNode && onUpdateNode(element.id, { ...element.data, config: { ...element.data.config, wait_config: { ...element.data.config.wait_config, duration_seconds: parseInt(e.target.value) || undefined } } })}
                      sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '13px', bgcolor: '#FFFFFF' } }}
                    />
                  </Box>
                )}

                {(element.data.type === 'agent' || element.data.type === 'tool' || element.data.type === 'conditional') && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', fontSize: '12px' }}>
                        Input Mappings ({editableInputMappingEntries.length})
                      </Typography>
                      {!readonly && (
                        <Button
                          size="small"
                          onClick={() => {
                            const prefix = element.data.type === 'tool' ? 'custom_' : 'param_';
                            const newKey = `${prefix}${editableInputMappingEntries.length + 1}`;
                            setEditableInputMappingEntries([...editableInputMappingEntries, { id: nextEntryId(), key: newKey, value: '' }]);
                          }}
                          sx={{ textTransform: 'none', fontSize: '11px', fontWeight: 700 }}
                        >
                          + Add {element.data.type === 'tool' ? 'Custom Mapping' : 'Mapping'}
                        </Button>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {editableInputMappingEntries.map((entry) => {
                        const isToolParam = isToolParameter(entry.key);
                        return (
                          <Paper
                            key={entry.id}
                            elevation={0}
                            sx={{
                              p: 1.5,
                              bgcolor: isToolParam ? '#F0F9FF' : '#F8FAFC',
                              border: `1px solid ${isToolParam ? '#BAE6FD' : '#E2E8F0'}`,
                              borderRadius: '6px',
                            }}
                          >
                            {isToolParam && (
                              <Chip
                                label="Tool Parameter"
                                size="small"
                                sx={{
                                  height: '18px',
                                  fontSize: '9px',
                                  fontWeight: 700,
                                  bgcolor: '#0EA5E9',
                                  color: '#FFFFFF',
                                  mb: 1,
                                }}
                              />
                            )}
                            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Parameter Name"
                                value={entry.key}
                                disabled={readonly || isToolParam}
                                onChange={(e) => {
                                  if (!isToolParam) {
                                    setEditableInputMappingEntries(prev =>
                                      prev.map(item => item.id === entry.id ? { ...item, key: e.target.value } : item)
                                    );
                                  }
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    fontSize: '12px',
                                    bgcolor: isToolParam ? '#F1F5F9' : '#FFFFFF',
                                  },
                                }}
                                helperText={isToolParam ? "Tool parameter (read-only)" : undefined}
                                FormHelperTextProps={{ sx: { fontSize: '10px', mt: 0.5 } }}
                              />
                              {!readonly && !isToolParam && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditableInputMappingEntries(prev => prev.filter(item => item.id !== entry.id));
                                  }}
                                  sx={{ color: '#EF4444' }}
                                >
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                            </Box>

                            <TextField
                              fullWidth
                              size="small"
                              label="Value (JSONPath or literal)"
                              value={entry.value}
                              disabled={readonly}
                              onChange={(e) => {
                                setEditableInputMappingEntries(prev =>
                                  prev.map(item => item.id === entry.id ? { ...item, value: e.target.value } : item)
                                );
                              }}
                              sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', bgcolor: '#FFFFFF' } }}
                              placeholder="$.steps.[step_id].output or literal value"
                              multiline
                              rows={2}
                            />
                          </Paper>
                        );
                      })}

                      {editableInputMappingEntries.length === 0 && (
                        <Typography sx={{ fontSize: '12px', color: '#94A3B8', fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                          No input mappings yet. Click "+ Add {element.data.type === 'tool' ? 'Custom' : ''} Mapping" to add one.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              // Edge Details
              <>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "#64748B",
                      fontSize: "12px",
                      display: "block",
                      mb: 0.75,
                    }}
                  >
                    Source Node
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "13px",
                      color: "#334155",
                    }}
                  >
                    {element.source}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "#64748B",
                      fontSize: "12px",
                      display: "block",
                      mb: 0.75,
                    }}
                  >
                    Target Node
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "13px",
                      color: "#334155",
                    }}
                  >
                    {element.target}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        )}

        {/* Save/Cancel Actions - Show for editable nodes with changes */}
        {isNode && hasUnsavedChanges && (
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              bgcolor: "#FFF9E6",
              borderTop: "2px solid #FFC107",
              boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
              zIndex: 10,
              mt: 3,
            }}
          >
            <Alert
              severity="warning"
              sx={{
                mb: 1.5,
                fontSize: "13px",
                "& .MuiAlert-icon": {
                  fontSize: "18px",
                },
              }}
            >
              You have unsaved changes
            </Alert>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                pt: 1,
                borderTop: "1px solid #F1F5F9",
              }}
            >
              <Button
                onClick={handleCancel}
                size="small"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "11px",
                  color: "#64748B",
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="primary"
                size="small"
                disableElevation
                startIcon={<SaveIcon />}
                onClick={handleSave}
              >
                Save Changes
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default WorkflowPropertiesDrawer;
